import { useEffect, useId, useRef, useState } from 'react';
import type { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@rocco/components/carousel';
import { RICH_TEXT_HTML_CLASS } from '~/ui-rocco/pages/detail-shared';

/**
 * "What our Clients say" — the professional's testimonials as clamped quote
 * cards with Load More. Rendered as an accordion row on the About tab; it lived
 * on the overview until the two pages split their content.
 *
 * Both tabs are served by the overview loader, so the data resolves the same
 * either side of the move.
 */
const TESTIMONIAL_CLAMP_LINES = 5;
/** Cards per view on desktop — what the arrows step by. */
const TESTIMONIALS_PER_VIEW = 3;
// Collapsed quote height: 5 lines at line-height 1.35. em-based so it tracks
// the responsive font size without a measurement flash. The card's own
// min-height keeps short testimonials from rendering as tiny cards.
const TESTIMONIAL_CLAMP_EM = '6.75em';

type AboutLoaderData = ReturnType<typeof useLoaderData<typeof loader>>;
export type Testimonial = NonNullable<
    AboutLoaderData['professionalOverview']['professionalTestimonials']
>[number];

const quoteGlyph =
    'font-sans leading-[0.5] text-[#d2d2d2] max-md:text-[2.5rem] md:text-[3.5rem]';

/**
 * Filled-out-of-five star rating; hidden when there's no rating.
 *
 * `Rating` is free text in the CMS, so a value that will not parse arrives here
 * as NaN — which compares false against every bound and would otherwise paint
 * five empty stars labelled "NaN out of 5". Clamped to 1–5 instead.
 *
 * `role="img"` is what makes the label announce: on a bare `div` the stars are
 * decorative children with nothing to hang a name on, so AT reads nothing.
 */
const StarRating = ({ rating }: { rating: number }) => {
    if (!Number.isFinite(rating)) return null;
    const rounded = Math.min(5, Math.round(rating));
    if (rounded <= 0) return null;
    return (
        <div
            role="img"
            className="flex items-center gap-1"
            aria-label={`${rounded} out of 5`}
        >
            {Array.from({ length: 5 }).map((_, index) => (
                <svg
                    key={index}
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                    className={cn(
                        'size-[0.95rem]',
                        index < rounded ? 'text-[#181818]' : 'text-[#d6d6d6]',
                    )}
                    fill="currentColor"
                >
                    <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L10 14.77l-5.2 2.73.99-5.78L1.58 7.62l5.82-.85L10 1.5z" />
                </svg>
            ))}
        </div>
    );
};

/**
 * One testimonial as a quote card. The quote is clamped to keep cards compact;
 * when it overflows, a Read more button reveals the rest in place. Only this
 * card grows — the grid is top-aligned so a neighbour in the same row keeps its
 * height.
 *
 * The control is a real button rather than a click handler on the card: the
 * card is a `figure`, so a handler there would leave the hidden text
 * unreachable by keyboard and give no indication it could be opened at all.
 */
const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [clampPx, setClampPx] = useState(0);
    const [fullPx, setFullPx] = useState(0);
    const quoteId = useId();

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;
        const measure = () => {
            const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 0;
            setClampPx(lineHeight * TESTIMONIAL_CLAMP_LINES);
            setFullPx(el.scrollHeight);
        };
        measure();
        document.fonts?.ready.then(measure).catch(() => {});
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const truncated = fullPx > clampPx + 1;

    return (
        // The min-height stops a one-line testimonial rendering as a stub, but
        // at 18rem it overshot the shortest cards by around 100px, and centring
        // split that into empty bands above and below the text — which the
        // corner quote marks then framed instead of the quote. 14rem still
        // reads as a card while sitting much closer to the content.
        <figure className="relative m-0 flex h-fit min-h-[12rem] flex-col justify-center rounded-tile-card bg-[#f6f6f6] md:min-h-[14rem]">
            {/* Corner quotes frame the centred content. The marks hang high in
                their box, so the top is nudged down to mirror the bottom's gap. */}
            <span
                aria-hidden="true"
                className={cn(
                    quoteGlyph,
                    'absolute left-[1.75rem] max-md:top-[2.1rem] md:top-[3rem]',
                )}
            >
                &ldquo;
            </span>
            <span
                aria-hidden="true"
                className={cn(
                    quoteGlyph,
                    'absolute right-[1.75rem] bottom-[1.5rem]',
                )}
            >
                &rdquo;
            </span>
            <div className="flex flex-col gap-[1rem] max-md:px-8 max-md:py-10 md:px-[3.5rem] md:py-[3.75rem]">
                {testimonial.Rating ? (
                    <StarRating rating={Number(testimonial.Rating)} />
                ) : null}
                {/* Clamp the collapsed quote to 5 lines; the max-height
                    transition animates the reveal. */}
                <div
                    id={quoteId}
                    ref={contentRef}
                    style={{
                        maxHeight: expanded
                            ? fullPx
                                ? fullPx
                                : undefined
                            : TESTIMONIAL_CLAMP_EM,
                    }}
                    className={cn(
                        'overflow-hidden font-twk-ghost leading-[1.35] font-normal text-[#1f1f21] transition-[max-height] duration-[360ms] ease-out motion-reduce:transition-none max-md:text-lg md:text-[1.5rem]',
                        RICH_TEXT_HTML_CLASS,
                    )}
                    dangerouslySetInnerHTML={{
                        __html: testimonial.Content ?? '',
                    }}
                />
                {truncated ? (
                    <button
                        type="button"
                        onClick={() => setExpanded((value) => !value)}
                        aria-expanded={expanded}
                        aria-controls={quoteId}
                        className="self-start font-sans font-medium text-[#181818] underline underline-offset-4 transition-opacity hover:opacity-70 max-md:text-sm md:text-base"
                    >
                        {expanded ? 'Read less' : 'Read more'}
                    </button>
                ) : null}
                {testimonial.Source || testimonial.Location ? (
                    <figcaption className="font-sans leading-tight max-md:text-sm md:text-base">
                        {testimonial.Source ? (
                            // The CMS stores Source as rich text, so it arrives
                            // wrapped in a block element. Left as-is that block
                            // breaks the line and strands the separator below at
                            // the start of the next one, so collapse its
                            // children inline.
                            <span
                                className="font-medium text-[#141414] [&_p]:m-0 [&_p]:inline"
                                dangerouslySetInnerHTML={{
                                    __html: testimonial.Source,
                                }}
                            />
                        ) : null}
                        {testimonial.Location ? (
                            // #767676 on this card's #f6f6f6 is 4.2:1, under the
                            // 4.5:1 AA floor for text this size. #6e6e6e clears
                            // it at 4.72:1 while staying visibly secondary.
                            <span className="text-[#6e6e6e]">
                                {testimonial.Source ? ' · ' : ''}
                                {testimonial.Location}
                            </span>
                        ) : null}
                    </figcaption>
                ) : null}
            </div>
        </figure>
    );
};

/**
 * (02) What our Clients say — the testimonials, paginated a screenful at a
 * time.
 *
 * Three to a view, contained in the page gutter rather than bleeding off it,
 * and the arrows step a whole row — the live profile's treatment. A drag-free
 * rail was tried first and read as a scrolling strip: these are read one at a
 * time, so a settled row the reader pages through beats a track that always
 * looks half-scrolled.
 *
 * Every testimonial is on the carousel — the old Load More paged four at a
 * time, which pagination has no need of.
 */
export const TestimonialsRow = ({
    testimonials,
}: {
    testimonials: Testimonial[];
}) => (
    <div className="pt-[0.75rem] pb-[1.8rem] max-md:pb-4">
        <Carousel
            ariaLabel="Client testimonials"
            options={{
                align: 'start',
                containScroll: 'trimSnaps',
                // A row at a time, so the reader never lands mid-card. Mobile
                // shows one, so `auto` there steps by the one that fits.
                slidesToScroll: 'auto',
            }}
        >
            {/* Only worth showing when there is a second page to reach. */}
            {testimonials.length > TESTIMONIALS_PER_VIEW ? (
                <div className="mb-[1.25rem] flex justify-end gap-[0.75rem]">
                    <CarouselPrevious className="md:size-11">
                        <ArrowLeft className="size-5" />
                    </CarouselPrevious>
                    <CarouselNext className="md:size-11">
                        <ArrowRight className="size-5" />
                    </CarouselNext>
                </div>
            ) : null}
            <CarouselContent className="max-md:gap-3 md:-mx-[0.375rem]">
                {testimonials.map((testimonial) => (
                    <CarouselItem
                        key={testimonial.ID}
                        // Thirds of the container, with the gap carried as
                        // padding so the row's outer edges stay on the gutter.
                        className="min-w-0 shrink-0 max-md:basis-[85%] md:basis-1/3 md:px-[0.375rem]"
                    >
                        <TestimonialCard testimonial={testimonial} />
                    </CarouselItem>
                ))}
            </CarouselContent>
        </Carousel>
    </div>
);
